import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  type DocumentData,
  type UpdateData,
  type QueryConstraint,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { logger, type LogSeverity } from '../../services/loggingService';

type SeverityFilter = LogSeverity | 'all';
type SeveritySelection = LogSeverity[];
type AttentionFilter = 'all' | 'needs_attention' | 'unresolved';
type SourceFilter = 'all' | 'frontend' | 'functions';

interface LogEntry {
  id: string;
  createdAt?: Date;
  severity: LogSeverity;
  source: string;
  message: string;
  context?: Record<string, unknown>;
  error?: Record<string, unknown> | null;
  route?: string | null;
  functionName?: string | null;
  userId?: string | null;
  attentionRequired?: boolean;
  acknowledged?: boolean;
  resolved?: boolean;
}

const severityColors: Record<LogSeverity, 'default' | 'info' | 'warning' | 'error' | 'secondary'> = {
  debug: 'default',
  info: 'info',
  warn: 'warning',
  error: 'error',
  critical: 'secondary',
};

const toDate = (value: unknown): Date | undefined => {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }
  return undefined;
};

const toRecord = (value: unknown): Record<string, unknown> | null => {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
};

const toLogEntry = (id: string, data: DocumentData): LogEntry => ({
  id,
  createdAt: toDate(data.createdAt ?? data.clientCreatedAt),
  severity: (data.severity as LogSeverity) || 'info',
  source: typeof data.source === 'string' ? data.source : 'unknown',
  message: typeof data.message === 'string' ? data.message : '',
  context: toRecord(data.context) ?? undefined,
  error: toRecord(data.error),
  route: typeof data.route === 'string' ? data.route : null,
  functionName: typeof data.functionName === 'string' ? data.functionName : null,
  userId: typeof data.userId === 'string' ? data.userId : null,
  attentionRequired: Boolean(data.attentionRequired),
  acknowledged: Boolean(data.acknowledged),
  resolved: Boolean(data.resolved),
});

const formatDate = (value?: Date): string => value?.toLocaleString() ?? '—';

const stringifyDetails = (value: unknown): string => {
  if (!value) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const LogsPanel: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [selectedSeverities, setSelectedSeverities] = useState<SeveritySelection>(['critical', 'error', 'warn', 'info', 'debug']);
  const [attentionFilter, setAttentionFilter] = useState<AttentionFilter>('unresolved');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [search, setSearch] = useState('');
  const [showTestLogs, setShowTestLogs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeFilterStart, setTimeFilterStart] = useState<Date | null>(null);
  const [timeFilterEnd, setTimeFilterEnd] = useState<Date | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedLogIds, setSelectedLogIds] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const constraints: QueryConstraint[] = [];
      if (selectedSeverities.length > 0) {
        constraints.push(where('severity', 'in', selectedSeverities));
      }
      if (sourceFilter !== 'all') {
        constraints.push(where('source', '==', sourceFilter));
      }
      if (attentionFilter === 'needs_attention') {
        constraints.push(where('attentionRequired', '==', true));
        constraints.push(where('acknowledged', '==', false));
      } else if (attentionFilter === 'unresolved') {
        constraints.push(where('resolved', '==', false));
      }
      constraints.push(orderBy('createdAt', 'desc'), limit(100));

      const unsubscribe = onSnapshot(
        query(collection(db, 'logs'), ...constraints),
        (snap) => {
          setLogs(snap.docs.map((entry) => toLogEntry(entry.id, entry.data())));
          setError(null);
          setLoading(false);
        },
        async (err) => {
          setError('Could not load logs. Check Firestore rules/indexes or try a different filter.');
          setLoading(false);
          await logger.error(
            '[LogsPanel] Failed to load logs',
            { selectedSeverities, attentionFilter, sourceFilter },
            { error: err, attentionRequired: true }
          );
        }
      );
      return unsubscribe;
    } catch (err) {
      setError('Could not load logs. Check Firestore rules/indexes or try a different filter.');
      await logger.error(
        '[LogsPanel] Failed to load logs',
        { selectedSeverities, attentionFilter, sourceFilter },
        { error: err, attentionRequired: true }
      );
    } finally {
      setLoading(false);
    }
  }, [attentionFilter, selectedSeverities, sourceFilter]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    loadLogs().then((cleanup) => {
      unsubscribe = cleanup;
    });
    return () => unsubscribe?.();
  }, [loadLogs]);

  const filteredLogs = useMemo(() => {
    let result = logs;
    
    // Filter by severity (client-side filter for multi-select)
    if (selectedSeverities.length > 0) {
      result = result.filter((entry) => selectedSeverities.includes(entry.severity));
    }
    
    // Filter by source
    if (sourceFilter !== 'all') {
      result = result.filter((entry) => entry.source === sourceFilter);
    }
    
    // Filter by time range
    if (timeFilterStart) {
      result = result.filter((entry) => entry.createdAt && entry.createdAt >= timeFilterStart);
    }
    if (timeFilterEnd) {
      result = result.filter((entry) => entry.createdAt && entry.createdAt <= timeFilterEnd);
    }
    
    // Filter out test logs if switch is off
    if (!showTestLogs) {
      result = result.filter((entry) => !entry.message.includes('*** TEST LOG ENTRY ***'));
    }
    
    // Apply search filter
    const term = search.trim().toLowerCase();
    if (term) {
      result = result.filter((entry) => {
        const haystack = [
          entry.message,
          entry.source,
          entry.route,
          entry.functionName,
          entry.userId,
          stringifyDetails(entry.context),
          stringifyDetails(entry.error),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(term);
      });
    }
    
    return result;
  }, [logs, search, showTestLogs, selectedSeverities, sourceFilter, timeFilterStart, timeFilterEnd]);

  const updateLog = async (entry: LogEntry, fields: UpdateData<DocumentData>) => {
    try {
      await updateDoc(doc(db, 'logs', entry.id), fields);
    } catch (err) {
      setError('Could not update log entry.');
      await logger.error('[LogsPanel] Failed to update log entry', { logId: entry.id }, { error: err });
    }
  };

  const paginatedLogs = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredLogs.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredLogs, page, rowsPerPage]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedLogIds(new Set(paginatedLogs.map((log) => log.id)));
    } else {
      setSelectedLogIds(new Set());
    }
  };

  const handleSelectLog = (logId: string, checked: boolean) => {
    const newSelected = new Set(selectedLogIds);
    if (checked) {
      newSelected.add(logId);
    } else {
      newSelected.delete(logId);
    }
    setSelectedLogIds(newSelected);
  };

  const handleBulkAcknowledge = async () => {
    for (const logId of Array.from(selectedLogIds)) {
      await updateDoc(doc(db, 'logs', logId), {
        acknowledged: true,
        acknowledgedAt: new Date().toISOString()
      });
    }
    setSelectedLogIds(new Set());
  };

  const handleBulkResolve = async () => {
    for (const logId of Array.from(selectedLogIds)) {
      await updateDoc(doc(db, 'logs', logId), {
        resolved: true,
        acknowledged: true,
        attentionRequired: false,
        resolvedAt: new Date().toISOString(),
        acknowledgedAt: new Date().toISOString()
      });
    }
    setSelectedLogIds(new Set());
  };

  const handleBulkUnacknowledge = async () => {
    for (const logId of Array.from(selectedLogIds)) {
      const log = paginatedLogs.find((l) => l.id === logId);
      // Only un-acknowledge if already acknowledged
      if (!log?.acknowledged) continue;
      const isHighSeverity = log?.severity === 'error' || log?.severity === 'critical';
      await updateDoc(doc(db, 'logs', logId), {
        acknowledged: false,
        acknowledgedAt: null,
        ...(isHighSeverity && { attentionRequired: true })
      });
    }
    setSelectedLogIds(new Set());
  };

  const handleBulkUnresolve = async () => {
    for (const logId of Array.from(selectedLogIds)) {
      const log = paginatedLogs.find((l) => l.id === logId);
      // Only un-resolve if already resolved
      if (!log?.resolved) continue;
      const isHighSeverity = log?.severity === 'error' || log?.severity === 'critical';
      await updateDoc(doc(db, 'logs', logId), {
        resolved: false,
        acknowledged: false,
        resolvedAt: null,
        acknowledgedAt: null,
        ...(isHighSeverity && { attentionRequired: true })
      });
    }
    setSelectedLogIds(new Set());
  };

  const handleBulkDelete = async () => {
    for (const logId of Array.from(selectedLogIds)) {
      await deleteDoc(doc(db, 'logs', logId));
    }
    setSelectedLogIds(new Set());
    setDeleteDialogOpen(false);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Logs</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="severity-filter-label">Severity</InputLabel>
          <Select
            labelId="severity-filter-label"
            label="Severity"
            multiple
            value={selectedSeverities}
            onChange={(event) => {
              const value = event.target.value;
              if ((value as SeveritySelection).includes('all' as LogSeverity)) {
                // Toggle all
                if (selectedSeverities.length === 5) {
                  setSelectedSeverities([]);
                } else {
                  setSelectedSeverities(['critical', 'error', 'warn', 'info', 'debug']);
                }
              } else {
                setSelectedSeverities(value as SeveritySelection);
              }
            }}
            renderValue={(selected) => (selected as SeveritySelection).length === 0 ? 'All' : (selected as SeveritySelection).join(', ')}
          >
            <MenuItem value="all">
              <ListItemIcon>
                <Checkbox checked={selectedSeverities.length === 5} indeterminate={selectedSeverities.length > 0 && selectedSeverities.length < 5} />
              </ListItemIcon>
              <ListItemText primary="Select All" />
            </MenuItem>
            <MenuItem value="critical">
              <Checkbox checked={selectedSeverities.includes('critical')} />
              Critical
            </MenuItem>
            <MenuItem value="error">
              <Checkbox checked={selectedSeverities.includes('error')} />
              Error
            </MenuItem>
            <MenuItem value="warn">
              <Checkbox checked={selectedSeverities.includes('warn')} />
              Warn
            </MenuItem>
            <MenuItem value="info">
              <Checkbox checked={selectedSeverities.includes('info')} />
              Info
            </MenuItem>
            <MenuItem value="debug">
              <Checkbox checked={selectedSeverities.includes('debug')} />
              Debug
            </MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel id="attention-filter-label">Attention</InputLabel>
          <Select
            labelId="attention-filter-label"
            label="Attention"
            value={attentionFilter}
            onChange={(event) => setAttentionFilter(event.target.value as AttentionFilter)}
          >
            <MenuItem value="all">All logs</MenuItem>
            <MenuItem value="needs_attention">Needs attention</MenuItem>
            <MenuItem value="unresolved">Unresolved</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="source-filter-label">Source</InputLabel>
          <Select
            labelId="source-filter-label"
            label="Source"
            value={sourceFilter}
            onChange={(event) => setSourceFilter(event.target.value as SourceFilter)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="frontend">Frontend</MenuItem>
            <MenuItem value="functions">Functions</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Start Date"
          type="date"
          value={timeFilterStart ? timeFilterStart.toISOString().split('T')[0] : ''}
          onChange={(event) => setTimeFilterStart(event.target.value ? new Date(event.target.value) : null)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small"
          label="End Date"
          type="date"
          value={timeFilterEnd ? timeFilterEnd.toISOString().split('T')[0] : ''}
          onChange={(event) => setTimeFilterEnd(event.target.value ? new Date(event.target.value) : null)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 260 }}
        />
        <Button variant="outlined" onClick={loadLogs} disabled={loading}>
          Refresh
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={async () => {
            await logger.debug('*** TEST LOG ENTRY *** Test debug from admin panel', { testField: 'testValue' }, { persist: true });
            await logger.info('*** TEST LOG ENTRY *** Test info from admin panel', { testField: 'testValue' }, { persist: true });
            await logger.warn('*** TEST LOG ENTRY *** Test warning from admin panel', { testField: 'testValue' });
            await logger.error('*** TEST LOG ENTRY *** Test error from admin panel', { testField: 'testValue' });
            await logger.critical('*** TEST LOG ENTRY *** Test critical from admin panel', { testField: 'testValue' });
            await logger.error('*** TEST LOG ENTRY *** Test with sensitive data', {
              password: 'secret123',
              token: 'abc-xyz-def',
              apiKey: 'secret-key',
              normalField: 'visible'
            });
            await loadLogs();
          }}
        >
          Seed Test Logs
        </Button>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="show-test-logs-label">Show test logs</InputLabel>
          <Select
            labelId="show-test-logs-label"
            label="Show test logs"
            value={showTestLogs ? 'yes' : 'no'}
            onChange={(event) => setShowTestLogs(event.target.value === 'yes')}
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>
        {selectedLogIds.size > 0 && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
            <Typography variant="body2">{selectedLogIds.size} selected</Typography>
            <Button size="small" onClick={handleBulkAcknowledge}>Acknowledge</Button>
            <Button size="small" onClick={handleBulkResolve}>Resolve</Button>
            <Button size="small" onClick={handleBulkUnacknowledge}>Un-acknowledge</Button>
            <Button size="small" onClick={handleBulkUnresolve}>Un-resolve</Button>
            <Button size="small" color="error" onClick={() => setDeleteDialogOpen(true)}>Delete</Button>
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading logs…</Typography>
        </Box>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedLogIds.size === paginatedLogs.length && paginatedLogs.length > 0}
                  indeterminate={selectedLogIds.size > 0 && selectedLogIds.size < paginatedLogs.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Context</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedLogs.map((entry) => (
              <TableRow
                key={entry.id}
                sx={entry.severity === 'critical' || entry.severity === 'error' ? { bgcolor: 'error.lighter' } : undefined}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedLogIds.has(entry.id)}
                    onChange={(e) => handleSelectLog(entry.id, e.target.checked)}
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(entry.createdAt)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={entry.severity}
                    color={severityColors[entry.severity] ?? 'default'}
                    variant={entry.severity === 'critical' ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell>{entry.functionName || entry.route || entry.source}</TableCell>
                <TableCell sx={{ minWidth: 240 }}>{entry.message}</TableCell>
                <TableCell>
                  <Box
                    component="pre"
                    sx={{
                      maxWidth: 360,
                      maxHeight: 160,
                      overflow: 'auto',
                      m: 0,
                      fontSize: 12,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {[stringifyDetails(entry.context), stringifyDetails(entry.error)].filter(Boolean).join('\n')}
                  </Box>
                </TableCell>
                <TableCell>
                  {entry.resolved ? (
                    <Chip size="small" label="Resolved" color="success" />
                  ) : entry.acknowledged ? (
                    <Chip size="small" label="Acknowledged" color="info" />
                  ) : entry.attentionRequired ? (
                    <Chip size="small" label="Needs attention" color="warning" />
                  ) : (
                    <Chip size="small" label="Open" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {!entry.acknowledged && (
                      <Button
                        size="small"
                        onClick={() => updateLog(entry, { acknowledged: true, acknowledgedAt: new Date().toISOString() })}
                      >
                        Acknowledge
                      </Button>
                    )}
                    {!entry.resolved && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          updateLog(entry, {
                            resolved: true,
                            acknowledged: true,
                            attentionRequired: false,
                            resolvedAt: new Date().toISOString(),
                            ...(entry.acknowledged ? {} : { acknowledgedAt: new Date().toISOString() })
                          })
                        }
                      >
                        Resolve
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {!loading && filteredLogs.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary">No logs match the current filters.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={filteredLogs.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete {selectedLogIds.size} log entries? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleBulkDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LogsPanel;
